import React, { useCallback, useEffect, useMemo } from 'react';
import { t } from '@citrite/translate';
import {
	Button,
	CopyActionIcon,
	DownloadActionIcon,
	layout,
	notifyError,
	notifySuccess,
	palette,
	styleUtils,
	typography,
} from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { trackAnalyticsEvent } from 'analytics';
import { useFormFactor } from 'App/Header/useFormFactor';
import { Recommendation, StyledButton } from 'App/ProgressAlert/Recommendation';
import { useRemediationActionResolver } from 'App/ProgressAlert/useRemediationActionResolver';
import {
	canShowFixIssueButton,
	extractErrorRemediationDetails,
	getClientVersionInfo,
	getErrorDetailsFromErrorMapper,
} from 'App/ProgressAlert/utils';
import { platformSpecificClientDisplayNames } from 'Components/Detection/nativeClientName';
import { useBranding } from 'Configuration/withBranding';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { getClientType } from 'Environment/getClientType';
import {
	getCurrentPlatform,
	isCitrixChromeApp,
	isNativeClient,
} from 'Environment/launchResource/device';
import { ErrorContext, getErrorMessage } from 'Environment/launchResource/launchUtil';
import { copyToClipboard } from 'Environment/utils';
import { traceManager } from 'Tracing';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { TroubleshootFixStatus } from 'Workspace/ResourceProvider/Context';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { TroubleshootEventPayload } from 'Workspace/TelemetryEvents/troubleshoot/troubleshootCASReporter';

export interface LaunchDetailsProps {
	transactionId: string;
	resource: Resource;
	errorMessage: string;
	isTroubleshoot?: boolean;
	onClose: (transactionId: string) => void;
}

const StyledDetailsContainer = styled.div<{ isSmallFormFactor?: boolean }>`
	display: flex;
	flex-direction: column;
	width: auto;
	border: ${props => (props.isSmallFormFactor ? 'none' : `1px ${palette.grey200} solid`)};
	padding: ${props =>
		props.isSmallFormFactor
			? `0 ${layout.mediumLargeSpace} 0 ${layout.mediumLargeSpace}`
			: `${layout.mediumSpace} ${layout.space12}`};
	gap: ${layout.space12};
	background-color: ${props =>
		props.isSmallFormFactor ? palette.white : `${palette.grey100}80`};
	${props => props.isSmallFormFactor && `margin-top: ${layout.mediumSpace}`}
`;

const StyledDetailsCard = styled.div`
	display: flex;
	flex-direction: column;
	border: 1px ${palette.grey200} solid;
	border-radius: ${layout.smallSpace};
	background-color: ${palette.white};
	box-shadow: ${styleUtils.shadow1.boxShadow};
`;

const StyledErrorMessageSection = styled.div<{ isTroubleshoot?: boolean }>`
	background-color: ${({ isTroubleshoot }) =>
		isTroubleshoot ? palette.yellow100 : palette.red100};
	padding: ${layout.smallSpace} ${layout.space12};
	word-wrap: normal;
	font-weight: ${typography.weights.regular};
	text-align: left;
	border-left: ${({ isTroubleshoot }) =>
		isTroubleshoot ? `2px solid ${palette.yellow900}` : `2px solid ${palette.red900}`};
	border-radius: 2px;
`;

const StyledErrorDetailsTable = styled.table`
	padding: ${layout.mediumSpace} ${layout.tinySpace};
	border-spacing: 0;
`;

const StyledTableRow = styled.tr`
	vertical-align: top;
`;

const StyledTableCell = styled.td`
	padding: 6px ${layout.smallSpace};
	vertical-align: top;
`;

const StyledErrorDetailsProperty = styled(StyledTableCell)`
	font-weight: ${typography.weights.regular};
	color: ${palette.grey700};
	white-space: nowrap;
`;

const StyledErrorDetailsValue = styled(StyledTableCell)`
	font-weight: ${typography.weights.regular};
`;

const StyledErrorDetailsActions = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-around;
	padding: ${layout.space12} ${layout.mediumLargeSpace};
	border-top: 1px ${palette.grey200} solid;
`;

const StyledVerticalDivider = styled.div`
	border-left: 1px ${palette.grey200} solid;
`;

interface ErrorDetailsRowProps {
	property: string;
	value: string;
}

const ErrorDetailRow: React.FC<ErrorDetailsRowProps> = ({ property, value }) => (
	<StyledTableRow>
		<StyledErrorDetailsProperty>{property}</StyledErrorDetailsProperty>
		<StyledErrorDetailsValue>{value}</StyledErrorDetailsValue>
	</StyledTableRow>
);

export const LaunchDetails: React.FC<LaunchDetailsProps> = ({
	transactionId,
	resource,
	errorMessage,
	onClose,
	isTroubleshoot = false,
}) => {
	const currentPlatform = getCurrentPlatform();
	const nativeClientName =
		platformSpecificClientDisplayNames.find(item => item.platform === currentPlatform)
			?.name || 'Windows';
	const { isSmallFormFactor } = useFormFactor();
	const [canGetLogs, setCanGetLogs] = React.useState(false);
	const accentColor = useBranding().styles?.AccentColor;

	useEffect(() => {
		const showGetLogs =
			!isCitrixChromeApp() &&
			(!isNativeClient()
				? traceManager?.isTracingEnabled()
				: environment?.supportsInitLogCollection);
		setCanGetLogs(showGetLogs);
	}, []);

	const getErrorDetails = useCallback(
		() => ({
			transactionId,
			errorMessage,
			platform: nativeClientName,
		}),
		[transactionId, errorMessage, nativeClientName]
	);

	const handleCopy = useCallback(async () => {
		trackAnalyticsEvent(TroubleshootEventPayload.copyDetails(getErrorDetails()));
		try {
			await copyToClipboard(JSON.stringify(getErrorDetails()));
			notifySuccess(t('Workspace:mandate_native_app_screen.url_copied'));
		} catch (error) {
			notifyError(t('Workspace:mandate_native_app_screen.url_copy_failed'));
		}
	}, [getErrorDetails]);

	const handleGetLogs = useCallback(async () => {
		trackAnalyticsEvent(TroubleshootEventPayload.getLogs(getErrorDetails()));
		if (!isNativeClient()) {
			await traceManager?.downloadTrace();
		} else {
			environment.initLogCollection();
		}
		notifySuccess(t('Workspace:desktop_launch_failures.logs_download_initiated'));
	}, [getErrorDetails]);

	const clientType = getClientType();
	const errorContext: ErrorContext = {
		clientType,
	};
	const { isLargeFormFactor } = useFormFactor();

	const isInspectConnectionEnabled = useFeatureCanary(
		FeatureFlag.EnableInspectConnection
	);

	const message = isInspectConnectionEnabled
		? getErrorDetailsFromErrorMapper(errorMessage)?.description ||
		  getErrorMessage(errorMessage, errorContext)
		: getErrorMessage(errorMessage, errorContext);

	const clientVersion = getClientVersionInfo();

	const { remediationInfo, remediationActionType, recommendationText } = useMemo(
		() => extractErrorRemediationDetails(errorMessage),
		[errorMessage]
	);

	const { remediationFunction, fixStatus } = useRemediationActionResolver(
		resource,
		remediationActionType,
		transactionId,
		errorMessage,
		onClose
	);

	const shouldShowRemediation =
		isInspectConnectionEnabled &&
		resource.supportsinspectconnection &&
		remediationFunction &&
		fixStatus === TroubleshootFixStatus.NOT_STARTED &&
		!isCitrixChromeApp();

	return (
		<StyledDetailsContainer isSmallFormFactor={isSmallFormFactor}>
			{isInspectConnectionEnabled && (
				<Recommendation
					recommendationText={recommendationText}
					remediationInfo={remediationInfo}
					remediationAction={remediationFunction}
					fixStatus={fixStatus}
					shouldShowRemediation={shouldShowRemediation}
				/>
			)}

			<StyledDetailsCard>
				<StyledErrorMessageSection isTroubleshoot={isTroubleshoot}>
					{message}
				</StyledErrorMessageSection>
				<StyledErrorDetailsTable>
					<tbody>
						<ErrorDetailRow
							property={t('Workspace:desktop_launch_failures.transaction_id')}
							value={transactionId}
						/>
						<ErrorDetailRow
							property={t('Workspace:desktop_launch_failures.platform_version')}
							value={`${nativeClientName} | ${clientVersion}`}
						/>
					</tbody>
				</StyledErrorDetailsTable>

				{(canGetLogs || !isCitrixChromeApp()) && (
					<StyledErrorDetailsActions>
						{!isCitrixChromeApp() && (
							<Button.IconTextButton
								icon={CopyActionIcon}
								onClick={handleCopy}
								title={t('Workspace:desktop_launch_failures.copy_details')}
								size={16}
								color={accentColor}
								text={t('Workspace:desktop_launch_failures.copy_details')}
								iconPosition="left"
							/>
						)}
						{canGetLogs && (
							<>
								<StyledVerticalDivider />
								<Button.IconTextButton
									icon={DownloadActionIcon}
									onClick={handleGetLogs}
									title={t('Workspace:desktop_launch_failures.get_logs')}
									size={16}
									color={accentColor}
									text={t('Workspace:desktop_launch_failures.get_logs')}
									iconPosition="left"
								/>
							</>
						)}
					</StyledErrorDetailsActions>
				)}
			</StyledDetailsCard>
			{remediationFunction && !isLargeFormFactor && canShowFixIssueButton(fixStatus) && (
				<StyledButton onClick={remediationFunction} isLargeFormFactor={isLargeFormFactor}>
					{t('Workspace:inspect_connection.fix_issue')}
				</StyledButton>
			)}
		</StyledDetailsContainer>
	);
};
